import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";

const STATS = [
  { value: "12,400+", label: "Active Workers" },
  { value: "3,200+", label: "Employers" },
  { value: "230+", label: "Cities Covered" },
  { value: "$4.2M+", label: "Paid Out to Workers" },
];

const VALUES = [
  { icon: "ri-flashlight-line", title: "Speed First", desc: "Every product decision optimizes for getting people earning faster. Urgency isn't a feature — it's our entire philosophy.", color: "bg-amber-50 text-amber-500" },
  { icon: "ri-brain-line", title: "AI That Actually Helps", desc: "We don't use AI as a buzzword. Every AI feature — matching, resumes, upskill — directly puts more money in workers' pockets.", color: "bg-emerald-50 text-emerald-600" },
  { icon: "ri-shield-user-line", title: "Workers First", desc: "We built the platform for the worker, not the employer. Fair pay, transparent fees, and no hidden deductions.", color: "bg-sky-50 text-sky-600" },
  { icon: "ri-community-line", title: "Real Opportunity", desc: "We believe anyone with a real skill should be able to turn it into real income — today, not next week.", color: "bg-violet-50 text-violet-600" },
];

const FEATURES = [
  {
    icon: "ri-ai-generate",
    title: "AI-Powered Matching",
    desc: "Our intelligent system analyzes your skills, experience, and preferences to connect you with the perfect opportunities — no more endless scrolling through irrelevant listings."
  },
  {
    icon: "ri-time-line",
    title: "Get Hired in Minutes",
    desc: "Traditional job hunting takes weeks. On Gig n' Cash, qualified workers can get matched and hired for urgent gigs within minutes of applying."
  },
  {
    icon: "ri-shield-check-line",
    title: "Verified & Trusted",
    desc: "Every worker and employer goes through our verification process. Reviews, ratings, and completed job history build trust on both sides."
  },
  {
    icon: "ri-wallet-3-line",
    title: "Fast, Secure Payments",
    desc: "Workers get paid quickly and securely. Employers only pay for completed work. Our payment system protects everyone involved."
  }
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-28 pb-20 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col lg:flex-row gap-12 items-center">
          <div className="flex-1">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-4">Our Story</span>
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-5 leading-tight">
              We Built Gig <span className="italic">n&apos;</span> Cash<br />
              <span className="text-emerald-500">Because People Can't Wait.</span>
            </h1>
            <p className="text-gray-500 text-lg leading-relaxed mb-6 max-w-xl">
              Gig <span className="italic">n&apos;</span> Cash started from a simple observation: millions of people have real skills and genuinely need work today — not in two weeks after five interviews and a background check backlog.
            </p>
            <p className="text-gray-500 text-base leading-relaxed max-w-xl mb-8">
              We built an AI-powered platform that closes the gap between "I need to earn" and "I'm earning" — measuring it in minutes, not days.
            </p>
            {/* Sign Up CTA added to hero */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Link 
                to="/signup" 
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm transition-colors whitespace-nowrap"
              >
                <i className="ri-user-add-line" />
                Get Started Free
              </Link>
              <Link 
                to="/how-it-works" 
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white hover:bg-gray-50 text-gray-700 font-bold rounded-xl text-sm border border-gray-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-circle-line" />
                See How It Works
              </Link>
            </div>
          </div>
          <div className="w-full lg:w-[480px] shrink-0 h-80 lg:h-[400px] rounded-2xl overflow-hidden">
            <img
              src="https://storage.readdy-site.link/project_files/a975f4d2-df36-474b-ab5c-75267cc8e7a8/c474769d-c39d-4148-8b8b-3f493fef8fc7_AdobeStock_763682486.jpeg?v=5f858ab979daf6b702e0cb98362adcda"
              alt="Gig n' Cash team and workers"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 bg-[#fafafa] border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-4xl font-black text-gray-900 mb-1">{s.value}</div>
                <div className="text-sm text-gray-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Introduction / Features Section */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-4">Why Gig n&apos; Cash</span>
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">The Platform Built for Real People</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              We combine cutting-edge AI with a human-first approach to create the fastest, fairest way to connect workers with income opportunities.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-5 p-6 rounded-2xl border border-gray-100 bg-[#fafafa] hover:border-emerald-200 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 flex-shrink-0">
                  <i className={`${f.icon} text-xl`} />
                </div>
                <div>
                  <h3 className="font-extrabold text-gray-900 text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-2">What We Stand For</h2>
            <p className="text-gray-500 text-base max-w-md mx-auto">The principles we refuse to compromise on, no matter how fast we grow.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {VALUES.map((v) => (
              <div key={v.title} className="p-6 rounded-2xl border border-gray-100 bg-[#fafafa]">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${v.color} mb-4`}>
                  <i className={`${v.icon} text-lg`} />
                </div>
                <h3 className="font-extrabold text-gray-900 text-sm mb-2">{v.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-500">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3">Join the Gig <span className="italic">n&apos;</span> Cash Movement</h2>
          <p className="text-emerald-100 mb-7 max-w-md mx-auto">Whether you need work or need workers — we built this for you.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" className="px-8 py-3.5 bg-white text-emerald-700 font-bold rounded-xl text-sm hover:bg-emerald-50 transition-colors whitespace-nowrap">
              <i className="ri-user-add-line mr-2" />Sign Up Free
            </Link>
            <Link to="/find-work" className="px-8 py-3.5 bg-white/10 border border-white/20 text-white font-bold rounded-xl text-sm hover:bg-white/20 transition-colors whitespace-nowrap">
              Find Work Today
            </Link>
            <Link to="/hire" className="px-8 py-3.5 bg-white/10 border border-white/20 text-white font-bold rounded-xl text-sm hover:bg-white/20 transition-colors whitespace-nowrap">
              Hire Workers
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
